
import {
  Component,
  ViewChild,
  AfterViewInit,
  OnInit,
  Input,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressBar } from "@angular/material/progress-bar";
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from "@angular/material/slide-toggle";
import { MatMenuModule } from "@angular/material/menu";
import { MatSortModule, MatSort } from "@angular/material/sort";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { Router, RouterModule } from "@angular/router";
import { Portal, PortalService } from "@kelyn-technologies/dsio-angular-lib/dsio-api";
import {
  SolutionService,
  Solution,
  UpdateSolutionForTenantRequest,
} from "@kelyn-technologies/dsio-angular-lib/dsio-api";
import { finalize } from "rxjs";

@Component({
    selector: "app-tenant-solutions",
    imports: [
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatProgressBar,
    MatSlideToggleModule,
    RouterModule,
    MatMenuModule,
    MatSnackBarModule,
],
    templateUrl: "./tenant-solutions.component.html",
    styleUrls: ["./tenant-solutions.component.css"]
})
export class TenantSolutionsComponent implements OnInit, AfterViewInit {
  @Input() public tenantId = "";
  solutionData: Solution[] = [];
  displayedColumns: string[] = ["name", "status", "action"];
  isloading = true;
  dataSource = new MatTableDataSource<Solution>(this.solutionData);
  disableInFlightSolutionIds = new Set<string>();

  constructor(
    private readonly solutionService: SolutionService,
    public router: Router,
    private readonly snackBar: MatSnackBar,
  ) {}

  @ViewChild(MatSort) sort!: MatSort;

  async ngOnInit(): Promise<void> {
    this.handleGetSolutions();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  async handleGetSolutions() {
    this.solutionService
      .getSolutionsByTenant(this.tenantId)
      .subscribe((response) => {
        this.solutionData = response.solutions;
        this.dataSource.data = this.solutionData;
        this.isloading = false;
      });
  }

  changeStatus(event: MatSlideToggleChange, solutionId: string) {
    const action = event.checked ? "enable" : "disable";
    this.solutionService
      .updateSolutionForTenant(
        solutionId,
        this.tenantId,
        { action: action } as UpdateSolutionForTenantRequest)
      .subscribe((response) => {
        this.updateSolutionStatus(solutionId, response.solution.status);
      });
  }

  disableSolution(solution: Solution) {
    if (!this.canDisableSolution(solution) || this.disableInFlightSolutionIds.has(solution.solution_id)) {
      return;
    }

    const confirmed = window.confirm(
      `Disable solution '${solution.name}' for tenant '${this.tenantId}'?`
    );
    if (!confirmed) {
      return;
    }

    this.disableInFlightSolutionIds.add(solution.solution_id);
    this.solutionService
      .updateSolutionForTenant(
        solution.solution_id,
        this.tenantId,
        { action: "disable" } as UpdateSolutionForTenantRequest
      )
      .pipe(finalize(() => this.disableInFlightSolutionIds.delete(solution.solution_id)))
      .subscribe({
        next: (response) => {
          this.updateSolutionStatus(solution.solution_id, response.solution.status);
          this.showMessage(`${solution.name} disable initiated.`);
        },
        error: () => {
          this.showMessage("Failed to disable solution. Please try again.");
        },
      });
  }

  isEnabled(solution: Solution) {
    switch (solution.status.toLowerCase()) {
      case "enabled":
        return true;
      case "enabling":
        return true;
      case "disabling":
        return true;
      case "unavailable":
        return true;
      default:
        return false;
    }
  }

  canToggleState(solution: Solution) {
    switch (solution.status.toLowerCase()) {
      case "configured":
        return true;
      default:
        return false;
    }
  }

  canDisableSolution(solution: Solution) {
    switch (solution.status.toLowerCase()) {
      case "configured":
      case "enabled":
      case "enabling":
      case "unavailable":
        return true;
      default:
        return false;
    }
  }

  private updateSolutionStatus(solutionId: string, status: string) {
    this.solutionData = this.solutionData.map((solution) => {
      if (solution.solution_id === solutionId) {
        return { ...solution, status };
      }
      return solution;
    });
    this.dataSource.data = this.solutionData;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, "Close", { duration: 5000 });
  }
}
